import type { z } from 'zod';

import type { FieldOption } from '../common/fields';
import type { ClassSchema } from './schemas';

type ClassField = keyof z.infer<typeof ClassSchema>;

// One-off enrols and makeups fill a lesson only to MAX_LESSON_SIZE and class students add up to
// MAX_CLASS_SIZE, so outside a roster changeover a lesson holds at most 24, and markRoll's
// transaction (the lesson plus an enrolment and a student per enrolled student) exceeds DynamoDB's
// 100-item limit only at 50 students
export const MAX_CLASS_SIZE = 12;
export const DESIRED_CLASS_SIZE = 9;
export const MAX_LESSON_SIZE = 12;
export const DESIRED_LESSON_SIZE = 9;

// The number of trial lessons a student can have before they are no longer considered a trial student
export const TRIAL_LESSONS_COUNT = 2;

// This limit exists as the lesson counter format is ####
export const MAX_LESSON_COUNT = 9999;

// The class query's filters, named as its route takes them
const queryAttributes = [
  'subject',
  'courseId',
  'streamId',
  'teacherId',
  'markerId',
  'day',
  'location',
  'roomId',
  'tag',
  'schoolId',
  'active',
] as const;
const listAttributes = [
  'id',
  'subject',
  'code',
  'label',
  'courseId',
  'streamId',
  'streamIds',
  'day',
  'time',
  'teacherId',
  'teacherName',
  'markerId',
  'markerName',
  'tags',
  'studentCount',
  'studentCap',
  'students',
  'active',
  'location',
  'roomId',
  'notes',
  'waitlist',
  'schoolTag',
  'slackChannelId',
  'slackChannelName',
  'allowMakeups',
] as const satisfies readonly ClassField[];
const getAttributes = [
  ...listAttributes,
  'permissionGroupId',
  'students',
  'remindBreaks',
] as const satisfies readonly ClassField[];
// `streamId` is the entry `streamIds` holds for the class course, written by the entity from the
// map; the API takes the map, never the stream on its own
const updateAttributes = [
  'courseId',
  'streamId',
  'streamIds',
  'day',
  'time',
  'teacherId',
  'teacherName',
  'markerId',
  'markerName',
  'notes',
  'tags',
  'studentCap',
  'location',
  'roomId',
  'remindBreaks',
  'schoolTag',
  'allowMakeups',
] as const satisfies readonly ClassField[];

export const ClassAttributes = {
  Query: queryAttributes,
  List: listAttributes,
  Get: getAttributes,
  Update: updateAttributes,
};

/** A class's day as its form offers it; choosing `null`, a day undetermined, clears it. */
export const CLASS_DAY_OPTIONS: readonly (FieldOption<number | null> & { shortLabel: string })[] = [
  { value: null, label: 'Undetermined', shortLabel: 'N/A' },
  { value: 1, label: 'Monday', shortLabel: 'Mon' },
  { value: 2, label: 'Tuesday', shortLabel: 'Tue' },
  { value: 3, label: 'Wednesday', shortLabel: 'Wed' },
  { value: 4, label: 'Thursday', shortLabel: 'Thu' },
  { value: 5, label: 'Friday', shortLabel: 'Fri' },
  { value: 6, label: 'Saturday', shortLabel: 'Sat' },
  { value: 0, label: 'Sunday', shortLabel: 'Sun' },
];
